import { useEffect, useRef } from 'react';

import { useRouter } from 'next/navigation';

import type JasperDefault from '@bellmedia/jasper-player-tv';
import {
  Accessibility,
  Advertising,
  Device,
  Discovery,
  Lifecycle,
  Localization,
  Metrics,
  Parameters,
} from '@firebolt-js/sdk';
import { setKeyMap } from '@noriginmedia/norigin-spatial-navigation';

import {
  LifecycleState,
  hasAppBeenForegrounded,
  updateLifecycleState,
  useAppLifecycle,
} from '@/hooks/useAppLifecycle';

import { TTS } from '@/components/Accessibility/speechSynthesizer';

import { IntentAction } from '@/enums';
import type { MetricPayload } from '@/utils/comcastMetricsDebugger';
import { deeplinkStorage } from '@/utils/deeplinkStorage';
import {
  clearInactiveReturnRoute,
  getCurrentRoute,
  isPlayerPlaying,
  markInactiveReturnRoute,
} from '@/utils/inactiveReturnRoute';
import { dispatchSessionInvalidated } from '@/utils/sessionInvalidated';

import packageJson from '../../package.json';
import { Keys, NetworkCallback, System } from '../index';

type FireboltNavigateIntent = {
  action?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
};

// ---------------------------------------------------------------------------
// In-flight request deduplication for Advertising.advertisingId()
// ---------------------------------------------------------------------------
// getDeviceAdId and getAdIdType both need data from the same single RPC call.
// When Promise.all fires them concurrently (as player/page.tsx does), the first
// caller creates the Promise and the second reuses it — one wire round-trip
// instead of two. The reference is cleared on settlement so the next ad break
// always gets a fresh fetch (no stale caching).
type AdvertisingIdResult = Awaited<ReturnType<typeof Advertising.advertisingId>>;
let advertisingIdInFlight: Promise<AdvertisingIdResult> | null = null;

function getAdvertisingId(): Promise<AdvertisingIdResult> {
  if (!advertisingIdInFlight) {
    advertisingIdInFlight = Advertising.advertisingId().finally(() => {
      advertisingIdInFlight = null;
    });
  }
  return advertisingIdInFlight;
}

// Firebolt rejections are JSON-RPC error objects ({code, message, data?}).
// This helper extracts the structured fields so the next on-device log capture immediately tells us
// which capability needs to be granted in the distributor App Manifest.
function logFireboltError(scope: string, err: unknown): void {
  const e = err as { code?: number; message?: string } | null | undefined;
  console.error(
    `[Firebolt] ${scope} failed:`,
    'code=',
    e?.code,
    'message=',
    e?.message,
    'raw=',
    err
  );
}

// ---------------------------------------------------------------------------
// Jasper player preload
// ---------------------------------------------------------------------------
// The Jasper bundle is ~5MB, so it is fetched and evaluated after the user has
// picked a profile and landed (see usePlayerPreload) rather than on the splash,
// and cached by the module system for the player page.
let isPlayerConfigured = false;
let playerPreload: Promise<void> | null = null;

// Jasper must be configured once before any player is created. The player calls
// this itself as well, so playback never depends on the preload having finished.
function configurePlayer(Jasper: typeof JasperDefault): void {
  if (isPlayerConfigured) return;
  isPlayerConfigured = true;
  // Replaces the removed window.Firebolt global
  Jasper.GlobalConfigurator.setFireboltSdk({ Device, Accessibility });
  Jasper.GlobalConfigurator.configure();
}

// Shared so repeated calls reuse one load; cleared on failure so the next call retries.
function preloadPlayer(): Promise<void> {
  if (!playerPreload) {
    playerPreload = import('@bellmedia/jasper-player-tv')
      .then(({ default: Jasper }) => configurePlayer(Jasper))
      .catch((err) => {
        playerPreload = null;
        logFireboltError('Jasper import', err);
      });
  }
  return playerPreload;
}

// Set to true when a warm deep-link (navigateTo) arrives so LifecycleManager
// knows to skip the inactive-resume redirect to SplashScreen.
let warmDeepLinkPending = false;
let pendingColdStartIntent: FireboltNavigateIntent | null = null;
let deepLinkHandlerRef: ((entityId: string, action?: IntentAction) => void) | null = null;
let lastHandledIntentKey: string | null = null;
let lastHandledIntentAt = 0;
const INTENT_DEDUPE_WINDOW_MS = 3000;

// Populated at runtime only when NEXT_PUBLIC_METRICS_DEBUGGER_ENABLED=true.
// Kept as a ref (not a static import) so webpack dead-code-eliminates the
// debugger module from the production bundle entirely.
let _logComcastMetric: ((eventName: string, payload: MetricPayload) => void) | null =
  null;
let _destroyMetricsDebugger: (() => void) | null = null;
const _metricsQueue: Array<{ eventName: string; payload: MetricPayload }> = [];

function _log(eventName: string, payload: MetricPayload): void {
  if (_logComcastMetric) {
    _logComcastMetric(eventName, payload);
  } else if (process.env.NEXT_PUBLIC_METRICS_DEBUGGER_ENABLED === 'true') {
    _metricsQueue.push({ eventName, payload });
  }
}

function LifecycleManager() {
  const router = useRouter();
  const hasCleanedUpForInactiveRef = useRef(false);
  const lifecycleState = useAppLifecycle();

  useEffect(() => {
    let resumeTimer: ReturnType<typeof setTimeout> | null = null;

    if (lifecycleState === 'foreground') {
      hasCleanedUpForInactiveRef.current = false;
      window.dispatchEvent(new Event('app:active'));
      window.dispatchEvent(new Event('app:resumeMedia'));

      if (
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).get('appState') === 'inactive'
      ) {
        // Defer by one macrotask so a concurrent navigateTo warm deep-link event
        // has time to set warmDeepLinkPending before we redirect to SplashScreen.
        resumeTimer = setTimeout(() => {
          if (warmDeepLinkPending) {
            warmDeepLinkPending = false;
            return;
          }
          const currentPath = window.location.pathname;
          const isOnDeepLink = currentPath === '/deep-link';
          if (!isOnDeepLink) {
            router.replace('/');
          }
        }, 0);
      }
    } else if (lifecycleState === 'background') {
      // Background = app shown as a "now playing" tile on the Xfinity home screen.
      // Playback must keep running, so do NOT dispatch app:pauseMedia here.
      window.dispatchEvent(new Event('app:background'));
    } else if (
      lifecycleState === 'inactive' &&
      hasAppBeenForegrounded() &&
      !hasCleanedUpForInactiveRef.current
    ) {
      hasCleanedUpForInactiveRef.current = true;

      // Capture before pausing: the pause flips the player state to PAUSED.
      const wasPlaying = isPlayerPlaying();
      console.log('[Lifecycle] inactive: wasPlaying =', wasPlaying);
      // A/V must be released while inactive; stop output right away instead of
      // waiting for the async cache flush and route change to unmount the player.
      window.dispatchEvent(new Event('app:pauseMedia'));

      const handleInactive = async () => {
        // Xi6 can emit an inactive->foreground bounce right after launch. If a
        // deep-link has already been resolved, do not force navigation to
        // /?appState=inactive or we can lose the deep-link flow.
        if (warmDeepLinkPending) {
          console.log(
            '[Firebolt] Skipping inactive splash redirect due to pending deep-link'
          );
          warmDeepLinkPending = false;
          return;
        }

        markInactiveReturnRoute(getCurrentRoute(), { wasPlaying });

        try {
          const { flushQueryClient } = await import('@/services/graphQL/queryClient');
          const { flushCache } = await import('@/services/graphQL');
          flushQueryClient();
          await flushCache();
        } catch (err) {
          console.error('[Lifecycle] Failed to flush cache:', err);
        }

        // clearStore() leaves the app query errored with no replacement fetch, so tell the
        // auth layer to move the session signal. Unconditional: a failed flush may still have
        // torn down the store, and an extra refetch is cheaper than a splash stuck on !app.
        dispatchSessionInvalidated();

        window.dispatchEvent(new Event('app:inactive'));
        router.replace('/?appState=inactive');
      };

      handleInactive();
    }

    return () => {
      if (resumeTimer) clearTimeout(resumeTimer);
    };
  }, [lifecycleState, router]);

  return null;
}

function registeredKeys() {
  return {
    UP: 38,
    DOWN: 40,
    LEFT: 37,
    RIGHT: 39,
    OK: 13,
    BACK: 8,
  };
}

interface FireboltLifecycleValue {
  state?: 'initializing' | 'inactive' | 'foreground' | 'background' | 'suspended';
  previous?: string;
}

const isComcastRuntime = () =>
  /X1|Xi\d|Xfinity|Comcast/i.test(navigator.userAgent) ||
  typeof (window as { XREReceiver?: unknown }).XREReceiver !== 'undefined' ||
  (navigator.userAgent.indexOf('WPE') !== -1 &&
    typeof (window as { AAMP?: unknown }).AAMP !== 'undefined');

// Firebolt returns a full BCP-47 tag (e.g. 'fr-CA'); reduce it to a bare ISO 639-1 code,
// same shape as navigator.language, for callers to map to a supported Language.
const toIsoLanguageCode = (locale: string): string => locale.split('-')[0].toLowerCase();

function safeDecodeDeepLinkValue(value: string): string {
  let decoded = value;
  for (let i = 0; i < 2; i += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }
  return decoded;
}

function extractEntityIdFromDeepLinkUrl(rawValue: string): string {
  const decoded = safeDecodeDeepLinkValue(rawValue).trim();

  if (!/^https?:\/\//i.test(decoded)) {
    return decoded;
  }

  try {
    const url = new URL(decoded);
    const queryParams = ['contentId', 'entityId', 'id', 'metadataId', 'content'];

    for (const key of queryParams) {
      const value = url.searchParams.get(key);
      if (value) {
        return safeDecodeDeepLinkValue(value.trim());
      }
    }

    const pathSegments = url.pathname.split('/').filter(Boolean);
    const maybeId = pathSegments[pathSegments.length - 1];
    if (maybeId && maybeId.toLowerCase() !== 'deep-link') {
      // Handle path segments that look like key=value (e.g. /entityId=58965 instead of ?entityId=58965)
      if (maybeId.includes('=')) {
        const eqIdx = maybeId.indexOf('=');
        const val = maybeId.slice(eqIdx + 1);
        if (val) return safeDecodeDeepLinkValue(val.trim());
      }
      return safeDecodeDeepLinkValue(maybeId.trim());
    }

    return decoded;
  } catch {
    return decoded;
  }
}

/**
 * This is a first-pass implementation to extract navigation intents from Firebolt deep links.
 * Supports entity/playback intents and voice guidance intents (home, search, section).
 * @link https://docs.developer.comcast.com/docs/deep-linking
 * @param value The intent from platform
 */
function extractNavigationIntent(value: FireboltNavigateIntent): {
  action: IntentAction;
  entityId?: string;
  query?: string;
  sectionName?: string;
} {
  if (!value.action) {
    console.error('[Firebolt] Received deep link with missing action:', value);
    return { action: IntentAction.NONE };
  }

  let action: IntentAction;

  // Handle home intent - no additional data required
  if (value.action === 'home') {
    return { action: IntentAction.HOME };
  }

  // Handle search intent - requires query in data
  if (value.action === 'search') {
    const query =
      value.data && typeof value.data.query === 'string' ? value.data.query : '';
    if (!query) {
      console.warn('[Firebolt] Search intent received but no query found:', value);
      return { action: IntentAction.NONE };
    }
    return { action: IntentAction.SEARCH, query };
  }

  // Handle section intent - requires sectionName in data
  if (value.action === 'section') {
    const sectionName =
      value.data && typeof value.data.sectionName === 'string'
        ? value.data.sectionName
        : '';
    if (!sectionName) {
      console.warn('[Firebolt] Section intent received but no sectionName found:', value);
      return { action: IntentAction.NONE };
    }
    return { action: IntentAction.SECTION, sectionName };
  }

  // Handle entity and playback intents - requires entityId in data
  const entityIdCandidate =
    value.data && typeof value.data.entityId === 'string' ? value.data.entityId : '';

  if (!entityIdCandidate) {
    console.error('[Firebolt] Received deep link with unrecognized format:', value);
    return { action: IntentAction.NONE };
  }

  switch (value.action) {
    case IntentAction.ENTITY:
      action = IntentAction.ENTITY;
      break;
    case IntentAction.PLAYBACK:
      action = IntentAction.PLAYBACK;
      break;
    default:
      action = IntentAction.NONE;
  }

  return {
    action,
    entityId: extractEntityIdFromDeepLinkUrl(entityIdCandidate),
  };
}

function handleResolvedDeepLinkIntent(
  intent: FireboltNavigateIntent,
  handleDeepLink: (
    entityId: string,
    action?: IntentAction,
    data?: { query?: string; sectionName?: string }
  ) => void,
  source: 'warm' | 'cold'
) {
  const extractedIntent = extractNavigationIntent(intent);

  if (extractedIntent.action === IntentAction.NONE) {
    console.warn(
      `[Firebolt] ${source} Deep Link received but no actionable intent found:`,
      intent
    );
    deeplinkStorage.clearProcessing();
    return;
  }

  // HOME, SEARCH, and SECTION intents don't require entityId validation
  // ENTITY and PLAYBACK intents require entityId
  if (
    (extractedIntent.action === IntentAction.ENTITY ||
      extractedIntent.action === IntentAction.PLAYBACK) &&
    !extractedIntent.entityId
  ) {
    console.warn(
      `[Firebolt] ${source} Deep Link received but no entityId found:`,
      intent
    );
    deeplinkStorage.clearProcessing();
    return;
  }

  if (!isComcastRuntime()) {
    console.warn('[Ignoring Intent] Returning early due to dummy intent from Firebolt.');
    deeplinkStorage.clearProcessing();
    return;
  }

  // Build deduplication key based on intent type
  let dedupeKey: string;
  if (extractedIntent.action === IntentAction.SEARCH) {
    dedupeKey = `${extractedIntent.action}:${extractedIntent.query}`;
  } else if (extractedIntent.action === IntentAction.SECTION) {
    dedupeKey = `${extractedIntent.action}:${extractedIntent.sectionName}`;
  } else if (extractedIntent.action === IntentAction.HOME) {
    dedupeKey = IntentAction.HOME;
  } else {
    dedupeKey = `${extractedIntent.action}:${extractedIntent.entityId}`;
  }

  const now = Date.now();
  if (
    lastHandledIntentKey === dedupeKey &&
    now - lastHandledIntentAt < INTENT_DEDUPE_WINDOW_MS
  ) {
    console.log(
      `[Firebolt] Skipping duplicate ${source} Deep Link within ${INTENT_DEDUPE_WINDOW_MS}ms:`,
      dedupeKey
    );
    return;
  }
  lastHandledIntentKey = dedupeKey;
  lastHandledIntentAt = now;

  // Signal LifecycleManager to skip the inactive-resume redirect and
  // clear the stale inactive return route so SplashScreen cannot navigate
  // to the previous session when a new deep-link has arrived.
  warmDeepLinkPending = true;
  clearInactiveReturnRoute();

  // Log the resolved intent with relevant data
  if (extractedIntent.action === IntentAction.SEARCH) {
    console.log(
      `[Firebolt] ${source} Deep Link resolved query:`,
      extractedIntent.query,
      'action:',
      extractedIntent.action
    );
  } else if (extractedIntent.action === IntentAction.SECTION) {
    console.log(
      `[Firebolt] ${source} Deep Link resolved sectionName:`,
      extractedIntent.sectionName,
      'action:',
      extractedIntent.action
    );
  } else if (extractedIntent.action === IntentAction.HOME) {
    console.log(
      `[Firebolt] ${source} Deep Link resolved action:`,
      extractedIntent.action
    );
  } else {
    console.log(
      `[Firebolt] ${source} Deep Link resolved entityId:`,
      extractedIntent.entityId,
      'action:',
      extractedIntent.action
    );
  }

  // Pass additional data for SEARCH and SECTION intents
  const additionalData = {
    ...(extractedIntent.query && { query: extractedIntent.query }),
    ...(extractedIntent.sectionName && { sectionName: extractedIntent.sectionName }),
  };

  handleDeepLink(
    extractedIntent.entityId || '',
    extractedIntent.action,
    Object.keys(additionalData).length > 0 ? additionalData : undefined
  );
}

export const system: System = {
  id: 'xfinity',
  init: function () {
    if (process.env.NEXT_PUBLIC_METRICS_DEBUGGER_ENABLED === 'true') {
      import('@/utils/comcastMetricsDebugger').then(
        ({ initMetricsDebugger, logComcastMetric, destroyMetricsDebugger }) => {
          _logComcastMetric = logComcastMetric;
          _destroyMetricsDebugger = destroyMetricsDebugger;
          initMetricsDebugger();
          _metricsQueue.forEach(({ eventName, payload }) =>
            _logComcastMetric!(eventName, payload)
          );
          _metricsQueue.length = 0;
        }
      );
    }

    // Keep default web key handling for first-pass xfinity support.
    setKeyMap({
      up: [38],
      down: [40],
      left: [37],
      right: [39],
      enter: [13],
    });

    // Detect the platform's real Voice Guidance setting instead of assuming it's always on.
    // Fetch the current value once, then listen
    // for the user toggling Voice Guidance while the app is running.
    const applyVoiceGuidanceSetting = ({ enabled }: { enabled: boolean }) => {
      system.overrideAria = enabled;
      if (enabled) TTS.init();
      else TTS.cancel();
    };

    // Prevent enabling voice guidance in a browser environment.
    // Firebolt returns true in the browser instead of failing, even though it's not running on a real device.
    if (isComcastRuntime()) {
      Accessibility.voiceGuidanceSettings()
        .then(applyVoiceGuidanceSetting)
        .catch((err) => logFireboltError('Accessibility.voiceGuidanceSettings', err));
      Accessibility.listen(
        'voiceGuidanceSettingsChanged',
        applyVoiceGuidanceSetting
      ).catch((err) =>
        logFireboltError("Accessibility.listen('voiceGuidanceSettingsChanged')", err)
      );
    }

    // Fetch Cold Start Parameters (Deep Linking/Privacy)
    Parameters.initialization()
      .then((initParams) => {
        console.log('[Firebolt] Init Params:', initParams);

        const coldStartIntent = initParams?.discovery?.navigateTo;
        if (coldStartIntent) {
          deeplinkStorage.setProcessing();
          console.log(
            '[Firebolt] Cold Start navigateTo from init params:',
            coldStartIntent
          );
          if (deepLinkHandlerRef) {
            handleResolvedDeepLinkIntent(coldStartIntent, deepLinkHandlerRef, 'cold');
          } else {
            console.log(
              '[Firebolt] Cold Start intent received before deep-link handler registered - deferring until initDeepLink'
            );
            pendingColdStartIntent = coldStartIntent;
          }
        } else {
          console.log('[Firebolt] No cold start navigateTo intent in init params');
        }
      })
      .catch((err) => logFireboltError('Parameters.initialization', err));

    // App State Transitions & Exit Handshake
    Lifecycle.listen((event: string, data: FireboltLifecycleValue) => {
      console.log('[Firebolt] Lifecycle event:', event, data);

      if (data.state) {
        updateLifecycleState(data.state as LifecycleState);
      }

      if (event === 'unloading') {
        // App is being closed, perform cleanup and then signal ready to exit
        _destroyMetricsDebugger?.();
        console.log('[Firebolt] Lifecycle unloading - calling finished()');
        Lifecycle.finished()
          .then(() => console.log('[Firebolt] finished() resolved'))
          .catch((err) => logFireboltError('Lifecycle.finished', err));
      }
    });

    // Signal App is Ready & Fire Launch Metrics
    Lifecycle.ready()
      .then(() => {
        console.log('[Firebolt] Lifecycle.ready ✓');
        _log('Metrics.appInfo', { version: this.version() });
        Metrics.appInfo(this.version())
          .then(() => console.log('[Firebolt] Metrics.appInfo ✓'))
          .catch((err) => logFireboltError('Metrics.appInfo', err));

        // Warm up the Advertising module by issuing each call once at launch.
        // Results are intentionally discarded - the getters below
        // (getDeviceAdId, getAdIdType, getIsLimitedAdTracking) always
        // re-fetch fresh on every read so privacy / ad-id changes propagate
        // to the next ad break without an app re-launch. This warmup pays
        // the cold-cost overhead (Ripple-side plugin activation,
        // distributor-backend IFA fetch) during app launch instead of
        // during player navigation, where it would otherwise delay first-frame.
        Advertising.advertisingId()
          .then(() => console.log('[Firebolt] Advertising.advertisingId warmup ✓'))
          .catch((err) => logFireboltError('Advertising.advertisingId (warmup)', err));
        Advertising.policy()
          .then(() => console.log('[Firebolt] Advertising.policy warmup ✓'))
          .catch((err) => logFireboltError('Advertising.policy (warmup)', err));

        // Warm up the Device identifier getters for the same reason: pay
        // the Ripple-side cold cost during app launch so the analytics
        // consumers (mParticle.setDeviceId, New Relic DEVICEID, OneTrust
        // GDPR consent receipt) pay warm wire time when they fire later.
        // getDeviceId tries Device.uid() first then falls back to
        // Device.id(); warm both so either path is hot.
        Device.uid()
          .then(() => console.log('[Firebolt] Device.uid warmup ✓'))
          .catch((err) => logFireboltError('Device.uid (warmup)', err));
        Device.id()
          .then(() => console.log('[Firebolt] Device.id warmup ✓'))
          .catch((err) => logFireboltError('Device.id (warmup)', err));

        if (isComcastRuntime()) {
          Advertising.listen('policyChanged', (data) => {
            console.log('[Firebolt] Advertising policyChanged:', data);
          })
            .then((listenerId) =>
              console.log(
                '[Firebolt] Advertising.listen(policyChanged) ✓ id=',
                listenerId
              )
            )
            .catch((err: { code?: number; message?: string } | null) =>
              console.warn(
                '[Firebolt] Advertising.listen(policyChanged) unavailable:',
                err?.code,
                err?.message
              )
            );
        }
      })
      .catch((err) => logFireboltError('Lifecycle.ready', err));
  },
  configurePlayer,
  preloadPlayer,
  version: () => `xfinity.${packageJson.version}`,
  script: () => <LifecycleManager />,
  keyThrottleDelay: 100,
  key: (keyboardEvent) => {
    const deviceKeys = registeredKeys();
    switch (keyboardEvent.keyCode) {
      case deviceKeys.UP:
        return Keys.UP;
      case deviceKeys.DOWN:
        return Keys.DOWN;
      case deviceKeys.LEFT:
        return Keys.LEFT;
      case deviceKeys.RIGHT:
        return Keys.RIGHT;
      case deviceKeys.OK:
        return Keys.OK;
      case 27:
      case deviceKeys.BACK:
        return Keys.BACK;
      default:
        return Keys.UNKNOWN;
    }
  },

  getDeviceId: async () => {
    // Primary source: Firebolt Device.uid() - a stable per-app+device
    // identifier gated by `xrn:firebolt:capability:device:uid`.
    //Preferred over Device.id() for privacy
    try {
      const uid = await Device.uid();
      if (uid) return uid;
    } catch (err) {
      logFireboltError('Device.uid', err);
    }
    // Fallback: Firebolt Device.id() - the cross-app platform back-office
    // device id, gated by `xrn:firebolt:capability:device:id`.
    try {
      const id = await Device.id();
      if (id) return id;
    } catch (err) {
      logFireboltError('Device.id', err);
    }
    // Ultimate fallback when both capability grants are missing or both
    // calls fail. Matches the pre-Firebolt placeholder behaviour so
    // analytics that already tolerate '' continue to do so.
    return '';
  },

  getErrorCodePrefix: () => 'F',
  closeApp: () => {
    // Tell the platform the user requested to exit
    console.log('[Firebolt] closeApp called - sending USER_EXIT to Lifecycle.close()');
    Lifecycle.close(Lifecycle.CloseReason.USER_EXIT)
      .then(() => console.log('[Firebolt] Lifecycle.close() resolved'))
      .catch((err) => logFireboltError('Lifecycle.close', err));
  },
  getDeviceAdId: async () => {
    // Fetch fresh on every read so privacy/ad-id changes propagate to the
    // next ad break without an app re-launch. Requires the distributor
    try {
      const { ifa } = await getAdvertisingId();
      return ifa;
    } catch (err) {
      logFireboltError('Advertising.advertisingId', err);
      return '';
    }
  },
  getAdIdType: async () => {
    try {
      const { ifa_type } = await getAdvertisingId();
      return ifa_type;
    } catch (err) {
      logFireboltError('Advertising.advertisingId', err);
      return '';
    }
  },
  getIsLimitedAdTracking: async () => {
    // Primary source: Advertising.policy().limitAdTracking - a live boolean
    // gated by `xrn:firebolt:capability:advertising:policy` (use).
    try {
      const policy = await Advertising.policy();
      if (typeof policy.limitAdTracking === 'boolean') {
        return policy.limitAdTracking;
      }
    } catch (err) {
      logFireboltError('Advertising.policy', err);
    }
    // Fallback: derive from Advertising.advertisingId().lmt ('0' | '1')
    // - gated by `xrn:firebolt:capability:advertising:identifier` (use).
    try {
      const { lmt } = await getAdvertisingId();
      if (lmt === '0' || lmt === '1') {
        return lmt === '1';
      }
    } catch (err) {
      logFireboltError('Advertising.advertisingId', err);
    }
    // Privacy-safe ultimate fallback when both capability grants are
    // missing or both calls fail for any other reason.
    return true;
  },
  monitorConnectionStatus: (callback: NetworkCallback) => {
    const onOffline = () => {
      callback(false);
    };
    const onOnline = () => {
      callback(true);
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  },
  hasPointer: false,
  initDeepLink: (handleDeepLink: (entityId: string, action?: IntentAction) => void) => {
    console.log('[Firebolt] initDeepLink listener registered');
    deepLinkHandlerRef = handleDeepLink;

    if (pendingColdStartIntent) {
      console.log(
        '[Firebolt] Processing deferred cold start intent now that handler is registered:',
        pendingColdStartIntent
      );
      handleResolvedDeepLinkIntent(pendingColdStartIntent, handleDeepLink, 'cold');
      pendingColdStartIntent = null;
    }

    const handleNavigateTo = (intent: FireboltNavigateIntent) => {
      deeplinkStorage.setProcessing();
      console.log('[Firebolt] Warm Deep Link navigateTo:', intent);
      handleResolvedDeepLinkIntent(intent, handleDeepLink, 'warm');
    };

    // Get the navigateTo callback ID, store it when we unload
    let navigateTo: number | null = null;
    Discovery.listen('navigateTo', handleNavigateTo)
      .then((sub) => (navigateTo = sub))
      .catch((err) => console.error('[Firebolt] Failed to listen for navigateTo:', err));

    return () => {
      deepLinkHandlerRef = null;
      if (navigateTo !== null) Discovery.clear(navigateTo);
    };
  },
  logError: (errorType, errorCode, errorMessage, isFatal, agePolicy) => {
    // Firebolt only accepts these 5 specific strings
    const validFireboltTypes = [
      'network',
      'media',
      'restriction',
      'entitlement',
      'other',
    ];

    // If an unsupported type is passed (like 'app' or 'api'), default to 'other'
    const mappedType = validFireboltTypes.includes(errorType)
      ? (errorType as Metrics.ErrorType)
      : Metrics.ErrorType.OTHER;

    console.log(
      `[Firebolt] Metrics.error type=${mappedType} code=${errorCode} fatal=${isFatal}`
    );
    _log('error', {
      type: mappedType,
      code: errorCode,
      message: errorMessage,
      fatal: isFatal,
      mediaSessionId: undefined,
      appSessionId: agePolicy,
    });
    Metrics.error(mappedType, errorCode, errorMessage, isFatal, undefined, agePolicy)
      .then(() => console.log('[Firebolt] Metrics.error resolved'))
      .catch((err) => logFireboltError('Metrics.error', err));
  },
  exitDirectly: true,
  logMediaEvent: (
    eventName,
    { entityId, position, agePolicy, bitrate, width, height, videoCodec }
  ) => {
    if (!entityId) return;
    switch (eventName) {
      case 'startContent':
        _log('Metrics.startContent', { entityId, agePolicy });
        Metrics.startContent(entityId, agePolicy)
          .then(() =>
            console.log('[Firebolt] Metrics.startContent resolved, agePolicy:', agePolicy)
          )
          .catch((err) => logFireboltError('Metrics.startContent', err));
        break;
      case 'stopContent':
        _log('Metrics.stopContent', { entityId, agePolicy });
        Metrics.stopContent(entityId, agePolicy)
          .then(() =>
            console.log('[Firebolt] Metrics.stopContent resolved, agePolicy:', agePolicy)
          )
          .catch((err) => logFireboltError('Metrics.stopContent', err));
        break;
      case 'play':
        _log('Metrics.mediaPlay', { entityId, agePolicy });
        Metrics.mediaPlay(entityId, agePolicy)
          .then(() =>
            console.log('[Firebolt] Metrics.mediaPlay resolved, agePolicy:', agePolicy)
          )
          .catch((err) => logFireboltError('Metrics.mediaPlay', err));
        break;
      case 'loadStart':
        _log('Metrics.mediaPlay', { entityId, agePolicy });
        _log('Metrics.mediaLoadStart', { entityId, agePolicy });
        Metrics.mediaPlay(entityId, agePolicy)
          .then(() => {
            console.log('[Firebolt] Metrics.mediaPlay resolved, agePolicy:', agePolicy);
            return Metrics.mediaLoadStart(entityId, agePolicy);
          })
          .then(() =>
            console.log(
              '[Firebolt] Metrics.mediaLoadStart resolved, agePolicy:',
              agePolicy
            )
          )
          .catch((err) =>
            logFireboltError('Metrics.mediaPlay/mediaLoadStart sequence', err)
          );
        break;
      case 'playing':
        _log('Metrics.mediaPlaying', { entityId, agePolicy });
        Metrics.mediaPlaying(entityId, agePolicy)
          .then(() =>
            console.log('[Firebolt] Metrics.mediaPlaying resolved, agePolicy:', agePolicy)
          )
          .catch((err) => logFireboltError('Metrics.mediaPlaying', err));
        break;
      case 'paused':
        _log('Metrics.mediaPause', { entityId, agePolicy });
        Metrics.mediaPause(entityId, agePolicy)
          .then(() =>
            console.log('[Firebolt] Metrics.mediaPause resolved, agePolicy:', agePolicy)
          )
          .catch((err) => logFireboltError('Metrics.mediaPause', err));
        break;
      case 'buffering':
        _log('Metrics.mediaWaiting', { entityId, agePolicy });
        Metrics.mediaWaiting(entityId, agePolicy)
          .then(() =>
            console.log('[Firebolt] Metrics.mediaWaiting resolved, agePolicy:', agePolicy)
          )
          .catch((err) => logFireboltError('Metrics.mediaWaiting', err));
        break;
      case 'ended':
        _log('Metrics.mediaEnded', { entityId, agePolicy });
        Metrics.mediaEnded(entityId, agePolicy)
          .then(() =>
            console.log('[Firebolt] Metrics.mediaEnded resolved, agePolicy:', agePolicy)
          )
          .catch((err) => logFireboltError('Metrics.mediaEnded', err));
        break;
      case 'seeking':
        _log('Metrics.mediaSeeking', {
          entityId,
          position: position ?? 0,
          agePolicy,
        });
        Metrics.mediaSeeking(entityId, position ?? 0, agePolicy)
          .then(() =>
            console.log(
              '[Firebolt] Metrics.mediaSeeking resolved, position:',
              position,
              'agePolicy:',
              agePolicy
            )
          )
          .catch((err) => logFireboltError('Metrics.mediaSeeking', err));
        break;
      case 'seeked':
        _log('Metrics.mediaSeeked', {
          entityId,
          position: position ?? 0,
          agePolicy,
        });
        Metrics.mediaSeeked(entityId, position ?? 0, agePolicy)
          .then(() =>
            console.log(
              '[Firebolt] Metrics.mediaSeeked resolved, position:',
              position,
              'agePolicy:',
              agePolicy
            )
          )
          .catch((err) => logFireboltError('Metrics.mediaSeeked', err));
        break;
      case 'renditionChange':
        _log('Metrics.mediaRenditionChange', {
          entityId,
          bitrate: bitrate ?? 0,
          width: width ?? 0,
          height: height ?? 0,
          videoCodec: videoCodec ?? '',
          agePolicy,
        });
        Metrics.mediaRenditionChange(
          entityId,
          bitrate ?? 0,
          width ?? 0,
          height ?? 0,
          videoCodec ?? '',
          agePolicy
        )
          .then(() =>
            console.log(
              '[Firebolt] Metrics.mediaRenditionChange resolved, bitrate:',
              bitrate,
              'width:',
              width,
              'height:',
              height,
              'agePolicy:',
              agePolicy
            )
          )
          .catch((err) => logFireboltError('Metrics.mediaRenditionChange', err));
        break;
    }
  },

  logMediaError: (errorCode, errorMessage, isFatal, agePolicy) => {
    _log('Metrics.error', {
      type: Metrics.ErrorType.MEDIA,
      code: errorCode,
      message: errorMessage,
      fatal: isFatal,
      mediaSessionId: undefined,
      appSessionId: agePolicy,
    });
    Metrics.error(
      Metrics.ErrorType.MEDIA,
      errorCode,
      errorMessage,
      isFatal,
      undefined,
      agePolicy
    )
      .then(() =>
        console.log('[Firebolt] Metrics.error (media) resolved, agePolicy:', agePolicy)
      )
      .catch((err) => logFireboltError('Metrics.error (media)', err));
  },

  getDeviceLanguage: async () => {
    try {
      const locale = await Localization.locale();
      return toIsoLanguageCode(locale);
    } catch (err) {
      logFireboltError('Localization.locale', err);
      return 'en';
    }
  },

  listenForDeviceLanguageChanges: (onChange) => {
    if (!isComcastRuntime()) return;

    let listenerId: number | null = null;
    let unsubscribed = false;

    Localization.listen('localeChanged', (locale: string) =>
      onChange(toIsoLanguageCode(locale))
    )
      .then((id) => {
        if (unsubscribed) {
          Localization.clear(id);
        } else {
          listenerId = id;
        }
      })
      .catch((err) => logFireboltError("Localization.listen('localeChanged')", err));

    return () => {
      unsubscribed = true;
      if (listenerId !== null) Localization.clear(listenerId);
    };
  },
};
